/**
 * @typedef Country
 *
 * @property {string} country_name
 * @property {string} country_handle
 * @property {string} store_handle
 */

/**
 * @typedef UserLocation
 *
 * @property {{
 *  country: {
 *    handle: string;
 *    name: string;
 *  };
 *  country_name: string;
 * }} detected_values
 */

/**
 * @typedef Store
 *
 * @property {string} title
 * @property {string} handle
 * @property {string} url
 * @property {boolean} isCurrent
 * @property {boolean} isEuStore
 * @property {boolean} isRestOfWorld
 * @property {string} flagCode
 * @property {string} [icon]
 * @property {string} currency
 */

/**
 * fetch() rejects only for network-level failures - most often because the
 * customer navigated away mid-request, which Safari reports as
 * "TypeError: Load failed". That isn't an application error, so swallow it
 * rather than leave an unhandled rejection for the error tracker to collect.
 *
 * @param {Error} error
 *
 * @returns {null}
 */
function ignoreNetworkFailure (error) {
  // eslint-disable-next-line no-console
  console.warn('Request failed:', error.message)

  return null
}

class StoreSelector extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['close', 'form', 'select']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.els.select.element, 'change', () =>
      this.handleSelectChange(),
    )

    this.listeners.add(this.els.close.element, 'click', () => {
      this.preferredStore = this.currentStore
      this.hasMadeDecision = this.currentStore
    })

    this.listeners.add(document, 'site-modal:closed', e => {
      if (e.target.id !== 'store-selector') {
        return
      }
      this.preferredStore = this.currentStore
      this.hasMadeDecision = this.currentStore
    })

    this.listeners.add(this.els.form.element, 'submit', () => {
      this.hasMadeDecision = this.selectedStore.value
    })

    this.initialise()
  }

  async initialise () {
    this.setPreferredStoreFromUrl()

    if (this.preferredStore) {
      return
    }

    const recommendedStore = await this.getRecommendedStoreForUsersCountry()
    this.updateSelect(recommendedStore)

    if (recommendedStore !== this.currentStore && !this.hasMadeDecision) {
      this.requestModalOpen()
    }
  }

  /** @param {string} value */
  updateSelect (value) {
    const select = this.els.select.element
    select.value = value
    /** @note Change event must be fired manually to ensure flag icon is updated */
    select.dispatchEvent(new Event('change'))
  }

  setPreferredStoreFromUrl () {
    const store = new URL(window.location).searchParams.get('store')

    if (store) {
      this.preferredStore = store
    }
  }

  handleSelectChange () {
    this.updateStoreIcon()
    this.updateBenefits()
  }

  async updateStoreIcon () {
    this.els['store-icon'].element.src = this.selectedStore?.dataset.flag
  }

  updateBenefits () {
    if (!this.els.benefits) {
      return
    }

    this.els.benefits.elements.forEach(panel => {
      const isSelectedStore = panel.dataset.handle.includes(
        this.selectedStore.value,
      )
      panel.setAttribute('aria-hidden', !isSelectedStore)
    })
  }

  async getRecommendedStoreForUsersCountry () {
    const [location] = await Promise.all([this.getLocationData()])

    const usersCountry = Array.from(this.els.select.element.options).find(
      country => country.value === location?.detected_values?.country?.handle,
    )?.value

    if (!usersCountry) {
      return 'GB'
    }

    return usersCountry
  }

  /** @returns {Promise<UserLocation>} */
  async getLocationData () {
    return fetch('/browsing_context_suggestions.json')
      .then(response => response.json())
      .catch(ignoreNetworkFailure)
  }

  requestModalOpen () {
    this.dispatchEvent(
      new CustomEvent('site-modal:open', {
        bubbles: true,
        detail: { id: 'store-selector' },
      }),
    )
  }

  /** @returns {Promise<Country[]>} */
  async getCountriesData () {
    return fetch(this.dataset.storeCountriesUrl)
      .then(response => response.json())
      .catch(ignoreNetworkFailure)
  }

  get currentStore () {
    return this.dataset.currentStore
  }

  get hasMadeDecision () {
    return window.localStorage.getItem('store-selector:selected')
  }

  set hasMadeDecision (selectedStore) {
    return window.localStorage.setItem('store-selector:selected', selectedStore)
  }

  get preferredStore () {
    return window.localStorage.getItem('store-selector:store')
  }

  set preferredStore (preferredStore) {
    return window.localStorage.setItem('store-selector:store', preferredStore)
  }

  get selectedStore () {
    return this.els.select.element.selectedOptions[0]
  }
}

if (!customElements.get('store-selector')) {
  customElements.define('store-selector', StoreSelector, { extends: 'aside' })
}
